import { ensureHttpsUrl, type ProbabilityEventDetailsDto } from '@sportspredict/shared';
import { getValidImgVariant } from '@/utils';
import type { GrandSlamResource } from '../constants/prestart.constants';
import {
	RESOURCES_COACHMARK_SEEN_KEY_PREFIX,
	RESOURCES_GUIDE_INTRO,
	RESOURCES_GUIDE_OPENED_KEY_PREFIX,
	RESOURCES_GUIDE_TITLE,
} from '../constants/resources-guide.constants';

/** Badge colors follow row position, regardless of the uploaded icon. */
const GUIDE_ICON_COLOR_CYCLE = [
	'border-[#E4D9FF] bg-[#E4D9FF]/64',
	'border-[#BDF4C3] bg-[#BDF4C3]/64',
	'border-[#DCE3F9] bg-[#EEF2FF]',
	'border-[#F5E4AB] bg-[#F5E4AB]/64',
] as const;

const getStorageScope = (eventId: string, userId?: string | number | null) =>
	`${eventId}:${userId ?? 'guest'}`;

export const getResourcesCoachmarkSeenKey = (eventId: string, userId?: string | number | null) =>
	`${RESOURCES_COACHMARK_SEEN_KEY_PREFIX}:${getStorageScope(eventId, userId)}`;

export const getResourcesGuideOpenedKey = (eventId: string, userId?: string | number | null) =>
	`${RESOURCES_GUIDE_OPENED_KEY_PREFIX}:${getStorageScope(eventId, userId)}`;

export const hasSeenResourcesCoachmark = (eventId: string, userId?: string | number | null) =>
	localStorage.getItem(getResourcesCoachmarkSeenKey(eventId, userId)) === '1';

export const markResourcesCoachmarkSeen = (eventId: string, userId?: string | number | null) => {
	localStorage.setItem(getResourcesCoachmarkSeenKey(eventId, userId), '1');
};

export const hasOpenedResourcesGuide = (eventId: string, userId?: string | number | null) =>
	localStorage.getItem(getResourcesGuideOpenedKey(eventId, userId)) === '1';

export const markResourcesGuideOpened = (eventId: string, userId?: string | number | null) => {
	localStorage.setItem(getResourcesGuideOpenedKey(eventId, userId), '1');
};

export const isResourcesGuideAnchorVisible = (element: HTMLElement | null): boolean => {
	if (!element?.isConnected) return false;

	let node: HTMLElement | null = element;
	while (node) {
		const style = window.getComputedStyle(node);
		if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
			return false;
		}
		node = node.parentElement;
	}

	const rect = element.getBoundingClientRect();
	if (rect.width < 1 || rect.height < 1) return false;

	return (
		rect.bottom > 0 &&
		rect.right > 0 &&
		rect.top < window.innerHeight &&
		rect.left < window.innerWidth
	);
};

export type ResolvedResourcesGuide = {
	title: string;
	intro: string;
	items: GrandSlamResource[];
	/** True when admin saved at least one resource section for this event. */
	hasConfiguredSections: boolean;
	coachmarkTitle: string;
	coachmarkBody: string;
	/** True when admin wrote tooltip copy for this event. No copy → no tooltip. */
	hasConfiguredCoachmark: boolean;
};

/** Map admin-configured guide content. Empty until Resources sections are saved. */
export const resolveResourcesGuide = (
	eventDetails?: ProbabilityEventDetailsDto | null
): ResolvedResourcesGuide => {
	const title = eventDetails?.resourcesGuideTitle?.trim() || RESOURCES_GUIDE_TITLE;
	const intro = eventDetails?.resourcesGuideIntro?.trim() || RESOURCES_GUIDE_INTRO;
	// Never fall back to stock copy: an event with no tooltip text gets no tooltip at all.
	const coachmarkTitle = eventDetails?.resourcesCoachmarkTitle?.trim() ?? '';
	const coachmarkBody = eventDetails?.resourcesCoachmarkBody?.trim() ?? '';
	const hasConfiguredCoachmark = Boolean(coachmarkTitle || coachmarkBody);
	const configuredItems = eventDetails?.resourcesGuideItems;
	const hasConfiguredSections = Array.isArray(configuredItems) && configuredItems.length > 0;

	if (!hasConfiguredSections) {
		return {
			title,
			intro,
			items: [],
			hasConfiguredSections: false,
			coachmarkTitle,
			coachmarkBody,
			hasConfiguredCoachmark,
		};
	}

	return {
		title,
		intro,
		items: configuredItems.map((item, index) => ({
			title: item.title,
			description: item.description,
			href: ensureHttpsUrl(item.href),
			// No upload → info glyph; the background still follows the row's position.
			icon: 'info' as const,
			iconClassName: GUIDE_ICON_COLOR_CYCLE[index % GUIDE_ICON_COLOR_CYCLE.length],
			iconUrl: getValidImgVariant(item.iconImgVariants),
		})),
		hasConfiguredSections: true,
		coachmarkTitle,
		coachmarkBody,
		hasConfiguredCoachmark,
	};
};
